import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Plus, 
  Trash2, 
  Variable,
  Users,
  FileText,
  AlertCircle,
  Sparkles,
  EyeOff,
  GripVertical,
  Settings,
  Wand2,
  CheckCircle,
  Info,
  Database,
  PanelRightOpen,
  PanelRightClose
} from 'lucide-react';
import api from '../../../lib/api';
import { ContractViewer } from '../../../components/contracts/ContractViewer';
import { RichTextEditor } from '../../../components/contracts/RichTextEditor';
import { SystemFieldsPanel } from '../../../components/contracts/SystemFieldsPanel';
import { Card, Button, Badge } from '../../../components/ui';

type TemplateCategory = 'CESSAO' | 'CUSTODIA' | 'ADESAO' | 'DISTRIBUICAO' | 'GESTAO';
type SignerRole = 'ORIGINADOR' | 'TENANT' | 'CUSTODIANTE' | 'INVESTIDOR' | 'TESTEMUNHA' | 'REPRESENTANTE';
type VariableType = 'string' | 'number' | 'date' | 'currency' | 'cpf' | 'cnpj';

interface TemplateVariable {
  name: string;
  type: VariableType;
  required: boolean;
  defaultValue?: string;
  validation?: string;
}

interface RequiredSigner {
  role: SignerRole;
  order: number;
  optional: boolean;
}

interface TemplateFormData {
  name: string;
  category: TemplateCategory;
  content: string;
  variables: TemplateVariable[];
  requiredSigners: RequiredSigner[];
}

const CATEGORY_OPTIONS: { value: TemplateCategory; label: string; description: string }[] = [
  { value: 'CESSAO', label: 'Cessão', description: 'Contratos de cessão de crédito ou direitos' },
  { value: 'CUSTODIA', label: 'Custódia', description: 'Contratos de custódia de ativos' },
  { value: 'ADESAO', label: 'Adesão', description: 'Termos de adesão e aceite' },
  { value: 'DISTRIBUICAO', label: 'Distribuição', description: 'Contratos de distribuição de rendimentos' },
  { value: 'GESTAO', label: 'Gestão', description: 'Contratos de gestão e administração' },
];

const SIGNER_ROLE_OPTIONS: { value: SignerRole; label: string; icon: string }[] = [
  { value: 'ORIGINADOR', label: 'Originador', icon: '🏢' },
  { value: 'TENANT', label: 'Empresa', icon: '🏛️' },
  { value: 'CUSTODIANTE', label: 'Custodiante', icon: '🔐' },
  { value: 'INVESTIDOR', label: 'Investidor', icon: '💰' },
  { value: 'TESTEMUNHA', label: 'Testemunha', icon: '👁️' },
  { value: 'REPRESENTANTE', label: 'Representante', icon: '👤' },
];

const VARIABLE_TYPE_OPTIONS: { value: VariableType; label: string; example: string }[] = [
  { value: 'string', label: 'Texto', example: 'João Silva' },
  { value: 'number', label: 'Número', example: '12345' },
  { value: 'date', label: 'Data', example: '01/01/2026' },
  { value: 'currency', label: 'Moeda', example: 'R$ 1.000,00' },
  { value: 'cpf', label: 'CPF', example: '123.456.789-00' },
  { value: 'cnpj', label: 'CNPJ', example: '12.345.678/0001-00' },
];

export function TemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id && id !== 'new';
  const [showPreview, setShowPreview] = useState(false);
  const [showSystemFields, setShowSystemFields] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'variables' | 'signers'>('content');
  const editorRef = useRef<{ insertText: (text: string) => void } | null>(null);

  const { register, control, handleSubmit, watch, setValue, formState: { errors, isDirty } } = useForm<TemplateFormData>({
    defaultValues: {
      name: '',
      category: 'CESSAO',
      content: '',
      variables: [],
      requiredSigners: [],
    },
  });

  const { fields: variableFields, append: appendVariable, remove: removeVariable } = useFieldArray({
    control,
    name: 'variables',
  });

  const { fields: signerFields, append: appendSigner, remove: removeSigner } = useFieldArray({
    control,
    name: 'requiredSigners',
  });

  const content = watch('content');
  const variables = watch('variables');
  const category = watch('category');

  // Load existing template
  const { data: template, isLoading } = useQuery({
    queryKey: ['contract-template', id],
    queryFn: async () => {
      const response = await api.get(`/contracts/templates/${id}`);
      return response.data;
    },
    enabled: isEditing,
  });

  // Set form values when template loads
  useEffect(() => {
    if (template) {
      setValue('name', template.name);
      setValue('category', template.category);
      setValue('content', template.content);
      setValue('variables', template.variables || []);
      setValue('requiredSigners', template.requiredSigners || []);
    }
  }, [template, setValue]);

  // Extract variables from content
  const detectedVariables = useMemo(() => {
    const regex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
    const plainContent = content.replace(/<[^>]*>/g, '');
    const matches = plainContent.matchAll(regex);
    return [...new Set([...matches].map(m => m[1]))];
  }, [content]);

  // Get list of variable names for the editor
  const variableNames = useMemo(() => {
    return variables.map(v => v.name).filter(Boolean);
  }, [variables]);

  // Create preview variables
  const previewVariables = useMemo(() => {
    const result: Record<string, string> = {};
    variables.forEach(v => {
      const typeConfig = VARIABLE_TYPE_OPTIONS.find(t => t.value === v.type);
      result[v.name] = v.defaultValue || typeConfig?.example || `[${v.name}]`;
    });
    return result;
  }, [variables]);

  // Undefined variables (in content but not defined)
  const undefinedVariables = useMemo(() => {
    return detectedVariables.filter(v => !variableNames.includes(v));
  }, [detectedVariables, variableNames]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      if (isEditing) {
        return api.put(`/contracts/templates/${id}`, data);
      }
      return api.post('/contracts/templates', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
      navigate('/admin/contracts/templates');
    },
  });

  const onSubmit = (data: TemplateFormData) => {
    saveMutation.mutate(data);
  };

  const handleAddVariable = () => {
    appendVariable({
      name: '',
      type: 'string',
      required: true,
      defaultValue: '',
    });
  };

  const handleAddSigner = () => {
    appendSigner({
      role: 'ORIGINADOR',
      order: signerFields.length,
      optional: false,
    });
  };

  const handleAutoDetectVariables = () => {
    undefinedVariables.forEach(varName => {
      appendVariable({
        name: varName,
        type: 'string',
        required: true,
        defaultValue: '',
      });
    });
  };

  // Função para inserir campo de sistema no editor
  const handleInsertSystemField = (fieldKey: string) => {
    const fieldTag = `{{${fieldKey}}}`;
    if (editorRef.current?.insertText) {
      editorRef.current.insertText(fieldTag);
    } else {
      // Fallback: adiciona ao final do conteúdo
      const currentContent = watch('content');
      setValue('content', currentContent + fieldTag, { shouldDirty: true });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-surface-900/95 backdrop-blur-sm border-b border-surface-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/contracts/templates')} 
              className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-surface-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-surface-100">
                {isEditing ? 'Editar Template' : 'Novo Template de Contrato'}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {template && (
                  <Badge variant="info" className="text-xs">v{template.version}</Badge>
                )}
                {isDirty && (
                  <span className="text-xs text-yellow-500 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse" />
                    Alterações não salvas
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={showSystemFields ? 'primary' : 'outline'}
              onClick={() => setShowSystemFields(!showSystemFields)}
              className="gap-2"
              title="Campos de Sistema (auto-preenchidos)"
            >
              {showSystemFields ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
              <Database className="w-4 h-4" />
            </Button>
            <Button
              variant={showPreview ? 'primary' : 'outline'}
              onClick={() => setShowPreview(!showPreview)}
              className="gap-2"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Ocultar' : 'Preview'}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={saveMutation.isPending}
              className="gap-2"
            >
              <Save className="w-4 h-4" />
              {saveMutation.isPending ? 'Salvando...' : 'Salvar Template'}
            </Button>
          </div>
        </div>
      </div>

      <div className={`p-6 grid gap-6 ${showPreview && showSystemFields ? 'grid-cols-[1fr_1fr_320px]' : showPreview ? 'grid-cols-2' : showSystemFields ? 'grid-cols-[1fr_320px]' : 'grid-cols-1'}`}>
        {/* Editor Column */}
        <div className="space-y-6">
          {/* Basic Info Card */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary-500/20 rounded-lg">
                <Settings className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-100">Informações Básicas</h3>
                <p className="text-sm text-surface-500">Defina o nome e categoria do template</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <label className="block text-sm text-surface-400 mb-2">Nome do Template</label>
                <input
                  {...register('name', { required: 'Nome é obrigatório' })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 text-surface-100 placeholder-surface-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                  placeholder="Ex: Contrato de Cessão de Crédito"
                />
                {errors.name && (
                  <p className="text-sm text-red-400 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm text-surface-400 mb-2">Categoria</label>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {CATEGORY_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        category === opt.value
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                      }`}
                    >
                      <input
                        type="radio"
                        {...register('category')}
                        value={opt.value}
                        className="sr-only"
                      />
                      <span className={`font-medium ${category === opt.value ? 'text-primary-400' : 'text-surface-200'}`}>
                        {opt.label}
                      </span>
                      <span className="text-xs text-surface-500 mt-1 line-clamp-2">{opt.description}</span>
                      {category === opt.value && (
                        <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-400" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Tabs Navigation */}
          <div className="flex items-center gap-1 p-1 bg-surface-800/50 rounded-xl">
            <button
              type="button"
              onClick={() => setActiveTab('content')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'content' 
                  ? 'bg-surface-700 text-surface-100 shadow-lg' 
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
              }`}
            >
              <FileText className="w-4 h-4" />
              Conteúdo
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('variables')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'variables' 
                  ? 'bg-surface-700 text-surface-100 shadow-lg' 
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
              }`}
            >
              <Variable className="w-4 h-4" />
              Variáveis
              {variableFields.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded-full">
                  {variableFields.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('signers')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'signers' 
                  ? 'bg-surface-700 text-surface-100 shadow-lg' 
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
              }`}
            >
              <Users className="w-4 h-4" />
              Signatários
              {signerFields.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                  {signerFields.length}
                </span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          <Card className="p-6">
            {/* Content Tab */}
            {activeTab === 'content' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-100">Conteúdo do Contrato</h3>
                      <p className="text-sm text-surface-500">Use {"{{variavel}}"} para campos dinâmicos</p>
                    </div>
                  </div>
                </div>
                
                <Controller
                  name="content"
                  control={control}
                  rules={{ required: 'Conteúdo é obrigatório' }}
                  render={({ field }) => (
                    <RichTextEditor
                      content={field.value}
                      onChange={field.onChange}
                      placeholder="Digite o conteúdo do contrato aqui..."
                      variables={variableNames}
                      minHeight="500px"
                    />
                  )}
                />
                
                {errors.content && (
                  <p className="text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.content.message}
                  </p>
                )}
                
                {undefinedVariables.length > 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                        <div>
                          <p className="text-sm font-medium text-yellow-400">
                            Variáveis detectadas não definidas
                          </p>
                          <p className="text-xs text-surface-400 mt-0.5">
                            {undefinedVariables.join(', ')}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAutoDetectVariables}
                        className="gap-2 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      >
                        <Wand2 className="w-4 h-4" />
                        Adicionar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Variables Tab */}
            {activeTab === 'variables' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Variable className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-100">Variáveis do Template</h3>
                      <p className="text-sm text-surface-500">Campos que serão preenchidos ao gerar contratos</p>
                    </div>
                  </div>
                  <Button type="button" size="sm" onClick={handleAddVariable} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Adicionar
                  </Button>
                </div>

                {variableFields.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-surface-700 rounded-xl">
                    <Variable className="w-12 h-12 mx-auto mb-3 text-surface-600" />
                    <p className="font-medium text-surface-300">Nenhuma variável definida</p>
                    <p className="text-sm text-surface-500 mt-1">
                      Use {"{{nome}}"} no conteúdo ou clique em adicionar
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {variableFields.map((field, index) => (
                      <div 
                        key={field.id} 
                        className="flex items-center gap-4 p-4 bg-surface-800/50 rounded-xl border border-surface-700 hover:border-surface-600 transition-colors"
                      >
                        <GripVertical className="w-4 h-4 text-surface-600 cursor-grab" />
                        
                        <div className="flex-1 grid grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs text-surface-500 mb-1">Nome da Variável</label>
                            <input
                              {...register(`variables.${index}.name`)}
                              placeholder="nome_variavel"
                              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-surface-500 mb-1">Tipo</label>
                            <select
                              {...register(`variables.${index}.type`)}
                              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 focus:border-primary-500"
                            >
                              {VARIABLE_TYPE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-surface-500 mb-1">Valor Padrão</label>
                            <input
                              {...register(`variables.${index}.defaultValue`)}
                              placeholder="Opcional"
                              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 focus:border-primary-500"
                            />
                          </div>
                          <div className="flex items-end pb-2">
                            <label className="flex items-center gap-2 text-sm cursor-pointer text-surface-300">
                              <input
                                type="checkbox"
                                {...register(`variables.${index}.required`)}
                                className="rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                              />
                              Obrigatório
                            </label>
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => removeVariable(index)}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Signers Tab */}
            {activeTab === 'signers' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <Users className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-100">Signatários Obrigatórios</h3>
                      <p className="text-sm text-surface-500">Quem precisa assinar este tipo de contrato</p>
                    </div>
                  </div>
                  <Button type="button" size="sm" onClick={handleAddSigner} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Adicionar
                  </Button>
                </div>

                {signerFields.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-surface-700 rounded-xl">
                    <Users className="w-12 h-12 mx-auto mb-3 text-surface-600" />
                    <p className="font-medium text-surface-300">Nenhum signatário definido</p>
                    <p className="text-sm text-surface-500 mt-1">
                      Adicione quem precisa assinar este contrato
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {signerFields.map((field, index) => {
                      return (
                        <div 
                          key={field.id} 
                          className="flex items-center gap-4 p-4 bg-surface-800/50 rounded-xl border border-surface-700 hover:border-surface-600 transition-colors"
                        >
                          <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 text-green-400 rounded-xl font-bold">
                            {index + 1}
                          </div>
                          
                          <div className="flex-1">
                            <select
                              {...register(`requiredSigners.${index}.role`)}
                              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 text-surface-100 focus:border-primary-500"
                            >
                              {SIGNER_ROLE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.icon} {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <label className="flex items-center gap-2 text-sm cursor-pointer text-surface-300 whitespace-nowrap">
                            <input
                              type="checkbox"
                              {...register(`requiredSigners.${index}.optional`)}
                              className="rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                            />
                            Opcional
                          </label>
                          
                          <button
                            type="button"
                            onClick={() => removeSigner(index)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {signerFields.length > 0 && (
                  <div className="p-4 bg-surface-800/30 rounded-xl flex items-start gap-3">
                    <Info className="w-5 h-5 text-surface-500 mt-0.5" />
                    <div className="text-sm text-surface-400">
                      <p>A ordem dos signatários define a sequência de assinatura.</p>
                      <p className="mt-1">Signatários opcionais podem ser removidos ao gerar o contrato.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div className="sticky top-24">
            <Card className="overflow-hidden">
              <div className="px-4 py-3 bg-surface-800 border-b border-surface-700 flex items-center gap-2">
                <Eye className="w-4 h-4 text-surface-400" />
                <span className="font-medium text-surface-200">Preview do Contrato</span>
              </div>
              <div className="h-[calc(100vh-200px)] overflow-auto">
                <ContractViewer
                  content={content}
                  variables={previewVariables}
                  status="DRAFT"
                />
              </div>
            </Card>
          </div>
        )}

        {/* System Fields Panel */}
        {showSystemFields && (
          <div className="sticky top-24">
            <Card className="overflow-hidden h-[calc(100vh-140px)]">
              <SystemFieldsPanel
                onInsertField={handleInsertSystemField}
                className="h-full"
              />
            </Card>
          </div>
        )}
      </div>

      {/* Save Error Toast */}
      {saveMutation.isError && (
        <div className="fixed bottom-6 right-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3 shadow-xl">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-300">Erro ao salvar template. Tente novamente.</p>
        </div>
      )}
    </div>
  );
}
